import type { SiteTemplateManifest } from "../types";

const shopContentBlocks = [
    "introHero",
    "columnsShowcase",
    "contentShowcase",
    "richContent",
    "text",
    "separator",
    "image",
    "video",
    "product",
    "icon",
    "accordion",
    "alert",
    "iconList",
    "rating",
    "card",
    "column",
    "share",
    "socialMedia",
    "facebook",
    "instagram",
    "searchBar",
    "textHighlight",
    "chart",
    "progressBar",
    "badge",
    "ctaBadge",
    "avatars",
    "blockquote",
    "form",
    "countdown",
    "code",
    "map",
    "appointmentButton",
    "donationButton",
    "addToCartButton",
    "supportedPaymentButton",
    "footerService",
    "footerLinks",
    "footerSocial",
    "footerApps",
    "footerCopyright",
    "heading",
    "paragraph",
    "button",
    "input",
    "textarea",
    "iconText",
    "socialLink",
    "serviceBenefitItem",
] as const;

const shopGlobalLayoutBlocks = ["promoBar", "header", "footer"] as const;

type ShopTemplatePageTypes = NonNullable<SiteTemplateManifest["pageTypes"]>;

/**
 * Keeps every shop template aligned with the complete storefront block engine.
 * A fresh object is returned so individual template manifests remain isolated.
 */
export function createShopTemplatePageTypes(): ShopTemplatePageTypes {
    const contentBlocks = [...shopContentBlocks];
    const globalLayoutBlocks = [...shopGlobalLayoutBlocks];

    return {
        home: {
            label: "Homepage",
            slots: [...contentBlocks, "heroSlider", "categoryRail", "productRail", "recentlyViewedProducts", "recommendedProducts", "campaignGrid", "brandGrid", "benefitStrip", "serviceBenefits", "newsletter", "seoText", ...globalLayoutBlocks],
        },
        category: {
            label: "Category",
            slots: [...contentBlocks, "breadcrumb", "categoryHero", "systemSubcategoryList", "productRail", "systemProductList", "seoText", ...globalLayoutBlocks],
            systemBlocks: ["systemSubcategoryList", "systemProductList"],
        },
        section: {
            label: "Section",
            slots: [...contentBlocks, "breadcrumb", "categoryHero", "systemSubcategoryList", "productRail", "systemProductList", "seoText", ...globalLayoutBlocks],
            systemBlocks: ["systemSubcategoryList", "systemProductList"],
        },
        product: {
            label: "Product",
            slots: [...contentBlocks, "breadcrumb", "systemProductGallery", "systemProductSummary", "systemProductBuyBox", "systemProductDescription", "relatedProducts", "seoText", ...globalLayoutBlocks],
            systemBlocks: ["systemProductGallery", "systemProductSummary", "systemProductBuyBox", "systemProductDescription"],
        },
        cart: {
            label: "Cart",
            slots: [...contentBlocks, "systemCartItems", "systemCartSummary", "benefitStrip", "productRail", ...globalLayoutBlocks],
            systemBlocks: ["systemCartItems", "systemCartSummary"],
        },
        checkout: {
            label: "Checkout",
            slots: [...contentBlocks, "systemCheckoutSteps", "systemCartSummary", "benefitStrip", ...globalLayoutBlocks],
            systemBlocks: ["systemCheckoutSteps", "systemCartSummary"],
        },
        customerAccount: {
            label: "Customer account",
            slots: [...contentBlocks, "systemAccountNavigation", "richContent", ...globalLayoutBlocks],
            systemBlocks: ["systemAccountNavigation"],
        },
        contact: {
            label: "Contact",
            slots: [...contentBlocks, "richContent", "contactInfo", "systemContactForm", ...globalLayoutBlocks],
            systemBlocks: ["systemContactForm"],
        },
        search: {
            label: "Search",
            slots: [...contentBlocks, "systemSearchForm", "systemProductList", "seoText", ...globalLayoutBlocks],
            systemBlocks: ["systemSearchForm", "systemProductList"],
        },
        storeList: {
            label: "Stores",
            slots: [...contentBlocks, "richContent", ...globalLayoutBlocks],
        },
        storeDetail: {
            label: "Store detail",
            slots: [...contentBlocks, "richContent", ...globalLayoutBlocks],
        },
        notFound: {
            label: "Not found",
            slots: [...contentBlocks, "richContent", "categoryRail", "productRail", ...globalLayoutBlocks],
        },
    };
}
